import type { FeedSuspensionEntry } from "@/types/ApiResponses/Sport";

/** A plain line/live switch write, keyed "<id>:<feed>". */
export interface OptimisticFeedSwitch {
  key: string;
  active: boolean;
}

/** An admin exception write, keyed "<id>:<feed>". */
export interface OptimisticFeedOverride {
  key: string;
  open: boolean;
}

/**
 * The editor's per-feed state right after a write, before the refetch lands —
 * shared by the sports / countries / leagues mutations so a switch flips the
 * same way on every page (rule 6). Pure: returns a new map.
 *
 * - Under an upper lock only the own closure can be cleared; the feed stays
 *   closed from above and the switch freezes until the refetch.
 * - An exception flips only the viewed target's effective state. Removing it
 *   drops back to closed only while a row above still closes (lockedBy); an
 *   orphaned one leaves the feed open.
 *
 * The refetch that follows is the source of truth either way.
 */
export const applyOptimisticFeedWrites = (
  previous: Record<string, FeedSuspensionEntry> | undefined,
  switches: readonly OptimisticFeedSwitch[],
  overrides: readonly OptimisticFeedOverride[]
): Record<string, FeedSuspensionEntry> => {
  const next = { ...(previous ?? {}) };

  for (const s of switches) {
    const prev = next[s.key];
    const lockedAbove = prev?.lockedBy != null;
    next[s.key] = lockedAbove
      ? { ...prev, active: true, editable: false, ownClosed: false }
      : { ...prev, active: s.active, editable: true, ownClosed: s.active };
  }

  for (const o of overrides) {
    const prev = next[o.key] ?? { active: false, editable: false };
    next[o.key] = {
      ...prev,
      active: o.open ? false : prev.lockedBy != null,
      override: o.open,
    };
  }

  return next;
};
